import type { ColumnFilterState, SortingState } from "../../types/table";
import {
  DEFAULT_OBSERVABILITY_MAIN_FILTERS,
  ObservabilityMainFilters,
  UsageActivityFeedColumnFilters,
} from "../../types/observability";
import {
  CUSTOM_DATE_INTERVAL,
  getDateRangeForInterval,
} from "./dateIntervalHelper";
import { parseSortingState } from "./sortingStorage";
import { readTenantFilters, writeTenantFilters } from "./filtersTenantStorage";

// One localStorage blob for everything the Observability dashboard should
// look the same with when the user comes back: the date filter plus both
// tables' column filters, column visibility and sort. Column pins keep their own keys.
// Stored per tenant and user, see filtersTenantStorage.
export const OBSERVABILITY_FILTERS_STORAGE_KEY = "filters:observability";

export interface PersistedObservabilityFilters {
  mainFilters: ObservabilityMainFilters;
  usageActivityFeed: {
    columnFilters: UsageActivityFeedColumnFilters | null;
    columnVisibility: Record<string, boolean> | null;
    /** undefined = nothing saved (the store keeps its default sort). */
    sort?: SortingState;
  };
  successFeesPerClaim: {
    columnFilters: Record<string, ColumnFilterState>;
    columnVisibility: Record<string, boolean> | null;
    sort?: SortingState;
  };
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);

// Presets are re-resolved against today so "This month" stays this month;
// only a custom range keeps its saved dates.
const parseMainFilters = (value: unknown): ObservabilityMainFilters => {
  if (!isPlainObject(value) || typeof value.dateInterval !== "string") {
    return { ...DEFAULT_OBSERVABILITY_MAIN_FILTERS };
  }

  if (value.dateInterval === CUSTOM_DATE_INTERVAL) {
    const { customDateFrom, customDateTo } = value;
    if (
      typeof customDateFrom === "string" &&
      typeof customDateTo === "string" &&
      customDateFrom &&
      customDateTo
    ) {
      return {
        dateInterval: CUSTOM_DATE_INTERVAL,
        customDateFrom,
        customDateTo,
      };
    }
    return { ...DEFAULT_OBSERVABILITY_MAIN_FILTERS };
  }

  // Unknown presets resolve to an empty range; the store then falls back to
  // its defaults (ensureObservabilityDateDefaults).
  const range = getDateRangeForInterval(value.dateInterval);
  if (!range.from || !range.to)
    return { ...DEFAULT_OBSERVABILITY_MAIN_FILTERS };
  return {
    dateInterval: value.dateInterval,
    customDateFrom: range.from,
    customDateTo: range.to,
  };
};

const parseVisibility = (value: unknown): Record<string, boolean> | null =>
  isPlainObject(value) ? (value as Record<string, boolean>) : null;

export const loadPersistedObservabilityFilters =
  (scope: string | null): PersistedObservabilityFilters => {
    const saved =
      typeof window !== "undefined"
        ? readTenantFilters(OBSERVABILITY_FILTERS_STORAGE_KEY, scope)
        : null;
    const blob = isPlainObject(saved) ? saved : {};
    const usage = isPlainObject(blob.usageActivityFeed)
      ? blob.usageActivityFeed
      : {};
    const fees = isPlainObject(blob.successFeesPerClaim)
      ? blob.successFeesPerClaim
      : {};

    const usageFilters = isPlainObject(usage.columnFilters)
      ? (usage.columnFilters as UsageActivityFeedColumnFilters)
      : null;

    return {
      mainFilters: parseMainFilters(blob.mainFilters),
      usageActivityFeed: {
        columnFilters:
          usageFilters && Object.keys(usageFilters).length > 0
            ? usageFilters
            : null,
        columnVisibility: parseVisibility(usage.columnVisibility),
        sort: parseSortingState(usage.sort),
      },
      successFeesPerClaim: {
        columnFilters: isPlainObject(fees.columnFilters)
          ? (fees.columnFilters as Record<string, ColumnFilterState>)
          : {},
        columnVisibility: parseVisibility(fees.columnVisibility),
        sort: parseSortingState(fees.sort),
      },
    };
  };

export const savePersistedObservabilityFilters = (
  scope: string | null,
  filters: PersistedObservabilityFilters,
): void => {
  writeTenantFilters(OBSERVABILITY_FILTERS_STORAGE_KEY, scope, filters);
};
